import type { ScheduleEntry } from '@myapp/contract';

/**
 * Whether this entry advertises Umdisponierung: the amber pill, the dashed outline, the ghost
 * hatch, the tooltip line.
 *
 * **A Pause never does**, even when the flag is set. Force-booking an absence over a materialised
 * break flags it — the conflict checker has no type filter — but a break is in no rescheduling
 * workflow: the Umdisponierung list and its nav badge query only APPOINTMENT / TASK / ABSENCE, and
 * the flag sweeper never clears it. The badge would point at an action that does not exist, over a
 * state the user cannot leave.
 *
 * Display only. `BoardBlock.needsRescheduling` stays the raw flag because `detectDragCollision`
 * reads it as *data*: a flagged entry does not reserve its time server-side, so suppressing it
 * there would make the client warn about a drop the API accepts.
 */
export function showsReschedulingState(
  entry: Pick<ScheduleEntry, 'type' | 'needsRescheduling'>,
): boolean {
  return entry.needsRescheduling && entry.type !== 'BREAK';
}

/**
 * The ghost fill for a block flagged `needsRescheduling`.
 *
 * Diagonal white hatching rather than a flat tint or a swapped colour: it washes the block out at
 * every width down to a 4-minute sliver, while the block keeps its own hue, so an appointment
 * awaiting a new slot is still recognisably an appointment. The colour it must not borrow is the one
 * `DragGhost` uses for a collision — this is not an error, it is unplaced work.
 */
export const RESCHEDULING_GHOST_HATCH =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.45) 0 4px, transparent 4px 8px)';

/** Amber, the same accent the Zeitmanagement list uses for `Neu planen`. */
export const RESCHEDULING_GHOST_OUTLINE = '2px dashed #F59E0B';
